import { useState } from "react";
import { RefreshCw } from "@/shared/ui/icons";
import { useTranslation } from "react-i18next";

import { useIsMobile } from "@/core/hooks";
import { Button, Infobulle } from "@/shared/ui";

/**
 * Bouton « Forcer la mise à jour » dans la TitleBar, à côté du badge
 * organisation.
 *
 * Pourquoi : le cockpit est une PWA. Quand une nouvelle release est
 * déployée, les assets sont servis par le service worker / le cache HTTP
 * jusqu'à un vrai hard refresh (Ctrl+Shift+R). Beaucoup d'utilisateurs ne
 * connaissent pas ce raccourci → ce bouton fait le hard refresh à leur
 * place, en un clic.
 *
 * Ce qu'il fait (best-effort, puis reload inconditionnel) :
 *   1. Désenregistre les service workers — garantit que la prochaine
 *      navigation repart du réseau (et non d'un index.html précaché périmé).
 *      Le SW se ré-enregistre tout seul au rechargement (cf PwaUpdateToast).
 *   2. Vide les Cache Storage (assets hashés de l'ancienne build).
 *   3. `window.location.reload()` — toujours, même si les étapes échouent.
 *
 * En Tauri (pas de SW, pas forcément de Cache API) les gardes `in`
 * sautent les étapes inapplicables et on retombe sur un simple reload de
 * la webview — inoffensif.
 */
export function HardRefreshButton() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [busy, setBusy] = useState(false);

  async function hardRefresh() {
    if (busy) return;
    setBusy(true);
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      // best-effort — on recharge quand même ci-dessous.
    } finally {
      window.location.reload();
    }
  }

  const libelle = t("shell.titleBar.hardRefresh", {
    defaultValue: "Forcer la mise à jour",
  });

  return (
    <Infobulle
      texte={t("shell.titleBar.hardRefreshTooltip", {
        defaultValue:
          "Forcer la mise à jour (vide le cache et recharge la dernière version)",
      })}
    >
      <Button
        variant="secondary"
        size={isMobile ? "lg" : "md"}
        iconOnly
        className={"shrink-0 " + (isMobile ? "h-9 w-9" : "")}
        onClick={() => void hardRefresh()}
        disabled={busy}
        aria-label={libelle}
      >
        <RefreshCw
          className={(isMobile ? "h-4 w-4 " : "h-3.5 w-3.5 ") + (busy ? "animate-spin" : "")}
        />
      </Button>
    </Infobulle>
  );
}
